import { useCallback, useEffect, useState } from "react";
import api from "../../../services/api";

const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toNumberOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const normalized = String(v).replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const initialBodyState = () => ({
  leftArmCm: "",
  rightArmCm: "",
  waistCm: "",
  hipCm: "",
  chestCm: "",
  leftThighCm: "",
  rightThighCm: "",
  leftCalfCm: "",
  rightCalfCm: "",
});

const resolveItemDate = (item) =>
  item?.date ?? item?.measuredAt ?? item?.createdAt ?? item?.loggedAt ?? null;

const toTime = (value) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const sortNewestFirst = (items = []) => {
  return [...items].sort((a, b) => {
    const dateDiff = toTime(resolveItemDate(b)) - toTime(resolveItemDate(a));
    if (dateDiff !== 0) return dateDiff;

    const idA = Number(a?.id ?? 0);
    const idB = Number(b?.id ?? 0);
    return idB - idA;
  });
};

export default function useReadingsData() {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [profileGender, setProfileGender] = useState("");

  const [weightDate, setWeightDate] = useState(todayISO());
  const [weightKg, setWeightKg] = useState("");
  const [weightNotes, setWeightNotes] = useState("");
  const [latestWeight, setLatestWeight] = useState(null);
  const [weightHistory, setWeightHistory] = useState([]);

  const [editingWeightId, setEditingWeightId] = useState(null);

  const [showBodyForm, setShowBodyForm] = useState(false);
  const [bodyDate, setBodyDate] = useState(todayISO());
  const [bodyNotes, setBodyNotes] = useState("");
  const [latestBody, setLatestBody] = useState(null);
  const [bodyHistory, setBodyHistory] = useState([]);

  const [editingBodyId, setEditingBodyId] = useState(null);

  const [body, setBody] = useState(initialBodyState);

  const clearMessageSoon = useCallback(() => {
    window.setTimeout(() => setMessage({ type: "", text: "" }), 3500);
  }, []);

  const resetBodyForm = useCallback(() => {
    setBodyNotes("");
    setBody(initialBodyState());
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);

    try {
      const prof = await api.get("/users/profile");
      const gender = prof?.data?.profile?.gender || "";
      setProfileGender(gender);

      const lw = await api.get("/measurements/weight/latest");
      const wh = await api.get("/measurements/weight");
      const sortedWeights = sortNewestFirst(wh?.data?.items || []);

      setWeightHistory(sortedWeights);
      setLatestWeight(sortedWeights[0] || lw?.data?.latest || null);

      const lb = await api.get("/measurements/body/latest");
      const bh = await api.get("/measurements/body");
      const sortedBody = sortNewestFirst(bh?.data?.items || []);

      setBodyHistory(sortedBody);
      setLatestBody(sortedBody[0] || lb?.data?.latest || null);
    } catch (e) {
      setMessage({
        type: "error",
        text: "Não consegui carregar seus dados agora. Tente novamente.",
      });
      clearMessageSoon();
    } finally {
      setLoading(false);
    }
  }, [clearMessageSoon]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // =========================
  // PESO
  // =========================

  const startEditWeight = (item) => {
    setEditingWeightId(item.id);
    setWeightDate(resolveItemDate(item)?.slice(0, 10));
    setWeightKg(String(item.weightKg || ""));
    setWeightNotes(item.notes || "");
  };

  const cancelEditWeight = () => {
    setEditingWeightId(null);
    setWeightKg("");
    setWeightNotes("");
    setWeightDate(todayISO());
  };

  const handleSaveWeight = useCallback(async () => {
    try {
      setMessage({ type: "", text: "" });

      const w = Number(String(weightKg).replace(",", "."));
      if (!w || !Number.isFinite(w) || w <= 0) {
        setMessage({ type: "error", text: "Informe um peso válido (kg)." });
        clearMessageSoon();
        return;
      }

      if (editingWeightId) {
        await api.put(`/measurements/weight/${editingWeightId}`, {
          date: weightDate,
          weightKg: w,
          notes: weightNotes || undefined,
        });

        setMessage({ type: "success", text: "Peso atualizado com sucesso." });
      } else {
        await api.post("/measurements/weight", {
          date: weightDate,
          weightKg: w,
          notes: weightNotes || undefined,
        });

        setMessage({ type: "success", text: "Peso registrado com sucesso." });
      }

      cancelEditWeight();
      clearMessageSoon();
      await loadAll();
    } catch (e) {
      const backendMsg = e?.response?.data?.message;
      setMessage({
        type: "error",
        text: backendMsg || "Erro ao salvar peso.",
      });
      clearMessageSoon();
    }
  }, [weightKg, weightDate, weightNotes, editingWeightId, clearMessageSoon, loadAll]);

  // =========================
  // MEDIDAS
  // =========================

  const startEditBody = (item) => {
    setEditingBodyId(item.id);
    setShowBodyForm(true);
    setBodyDate(resolveItemDate(item)?.slice(0, 10));
    setBodyNotes(item.notes || "");

    setBody({
      leftArmCm: item.leftArmCm ?? "",
      rightArmCm: item.rightArmCm ?? "",
      waistCm: item.waistCm ?? "",
      hipCm: item.hipCm ?? "",
      chestCm: item.chestCm ?? "",
      leftThighCm: item.leftThighCm ?? "",
      rightThighCm: item.rightThighCm ?? "",
      leftCalfCm: item.leftCalfCm ?? "",
      rightCalfCm: item.rightCalfCm ?? "",
    });
  };

  const cancelEditBody = () => {
    setEditingBodyId(null);
    resetBodyForm();
  };

  const handleSaveBody = useCallback(async () => {
    try {
      setMessage({ type: "", text: "" });

      const payload = {
        date: bodyDate,
        notes: bodyNotes || undefined,
        ...Object.fromEntries(
          Object.entries(body).map(([k, v]) => [k, toNumberOrNull(v)])
        ),
      };

      if (editingBodyId) {
        await api.put(`/measurements/body/${editingBodyId}`, payload);
        setMessage({ type: "success", text: "Medidas atualizadas." });
      } else {
        await api.post("/measurements/body", payload);
        setMessage({ type: "success", text: "Medidas registradas." });
      }

      cancelEditBody();
      setShowBodyForm(false);

      clearMessageSoon();
      await loadAll();
    } catch (e) {
      const backendMsg = e?.response?.data?.message;
      setMessage({
        type: "error",
        text: backendMsg || "Erro ao salvar medidas.",
      });
      clearMessageSoon();
    }
  }, [body, bodyDate, bodyNotes, editingBodyId, clearMessageSoon, loadAll]);

  return {
    loading,
    message,

    profileGender,

    weightDate,
    setWeightDate,
    weightKg,
    setWeightKg,
    weightNotes,
    setWeightNotes,
    latestWeight,
    weightHistory,
    editingWeightId,
    startEditWeight,
    cancelEditWeight,

    showBodyForm,
    setShowBodyForm,
    bodyDate,
    setBodyDate,
    bodyNotes,
    setBodyNotes,
    latestBody,
    bodyHistory,
    body,
    setBody,
    editingBodyId,
    startEditBody,
    cancelEditBody,

    handleSaveWeight,
    handleSaveBody,
  };
}